// Angular
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * A custom validator that applies a minimum length to the trimmed control value, so padding
 * cannot satisfy the rule (`"A "` fails a minimum of 2 where Angular's `Validators.minLength`
 * would pass it).
 *
 * Emits Angular's own `minlength` error key and payload shape so the component library renders
 * its existing "does not contain enough characters" message without any template change.
 *
 * An empty value returns null — pair this with `Validators.required` when the field is mandatory.
 * A whitespace-only value fails with `actualLength: 0`, since `required` lets it through.
 *
 * @param requiredLength The minimum number of non-whitespace-padded characters.
 * @returns A `minlength` ValidationError if the trimmed value is too short, otherwise null.
 */
export function minTrimmedLengthValidator(requiredLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Nothing entered at all — `required` owns that case. A whitespace-only value is NOT empty
    // to `required` (it checks raw length, not trimmed), so it has to fail here instead.
    if (typeof value !== 'string' || value.length === 0) {
      return null;
    }

    const actualLength = value.trim().length;

    if (actualLength < requiredLength) {
      return { minlength: { requiredLength, actualLength } };
    }

    return null;
  };
}
